/**
 * 6sense account Audience Builder API client (mb-frontend#3391).
 *
 * Targets the dedicated backend at /private/sixsense-audience — NOT the pre-existing
 * 6sense *enrichment* integration (services/integrations.ts getSixsenseAuthStatus).
 * Every call is workspace-scoped + gated server-side on an active 6sense token.
 */

import { apiInstance } from '@/utils';
import { REQUEST_METHOD } from '@/constants';

const BASE = '/private/sixsense-audience';

export interface SixsenseField {
  name: string;
  description: string;
  data_type: 'string' | 'number' | 'date' | 'picklist' | 'boolean' | 'segment';
  category: string;
  group: string;
  operators: string[];
  placeholder?: string;
  // Plain enum values, or { value: stableId, label: name } pairs for segment fields.
  picklist_values?: (string | number | { value: string; label: string })[] | null;
  available: boolean;
  availability_reason: string | null;
}

export interface SixsenseFilterCondition {
  id: string;
  field: string;
  operator: string;
  value?: unknown;
}

export interface SixsenseFilterGroup {
  /** Client-only stable React key; ignored by the backend compiler. */
  id?: string;
  group_operator: 'and' | 'or';
  logical_operator: 'and' | 'or';
  conditions: SixsenseFilterCondition[];
}

export interface SixsensePreviewResult {
  success: boolean;
  count: number;
  sample: Record<string, unknown>[];
}

export const getSixsenseAudienceAuthStatus = async (): Promise<{ success: boolean; connected: boolean }> => {
  const res = await apiInstance({ method: REQUEST_METHOD.GET, url: `${BASE}/auth-status` });
  return res.data;
};

export const getSixsenseFields = async (): Promise<{ success: boolean; fields: SixsenseField[]; capability_summary: { total: number; available: number } }> => {
  const res = await apiInstance({ method: REQUEST_METHOD.GET, url: `${BASE}/fields` });
  return res.data;
};

export const previewSixsenseAudience = async (filterGroups: unknown[]): Promise<SixsensePreviewResult> => {
  const res = await apiInstance({ method: REQUEST_METHOD.POST, url: `${BASE}/preview`, data: { filter_groups: filterGroups } });
  return res.data;
};

// Accounts whose domain already matched a record in the vendor's own customers table are
// never added here -- they're routed to the vendor's accumulating "Existing Customers"
// list instead (mb-server#4674: a wasted find otherwise, since they're already a customer).
export interface SixsenseMaterializeResult {
  success: boolean;
  audience_id: number;
  list_id: number;
  account_count: number;
  existing_customers_excluded: number;
  existing_customers_audience_id: number | null;
}

export const createSixsenseAudience = async (
  payload: { name: string; filter_groups: unknown[] },
): Promise<SixsenseMaterializeResult> => {
  const res = await apiInstance({ method: REQUEST_METHOD.POST, url: BASE, data: payload });
  return res.data;
};

export const refreshSixsenseAudience = async (
  id: number,
  opts: { filterGroups?: unknown[]; name?: string } = {},
): Promise<SixsenseMaterializeResult> => {
  const data: Record<string, unknown> = {};
  if (opts.filterGroups) data.filter_groups = opts.filterGroups;
  if (opts.name != null) data.name = opts.name;
  const res = await apiInstance({ method: REQUEST_METHOD.POST, url: `${BASE}/${id}/refresh`, data });
  return res.data;
};

export const getSixsenseAudience = async (id: number): Promise<{ success: boolean; audience: { id: number; name: string; list_id: number; rules: SixsenseFilterGroup[] } }> => {
  const res = await apiInstance({ method: REQUEST_METHOD.GET, url: `${BASE}/${id}` });
  return res.data;
};
